import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { artistArrears } from '@/domain/artistArrears';
import type { StudioId } from '@/domain/types';
import { dayKey } from '@/utils/studioDay';

/**
 * "Still owed to artists" (W7 n4): Finance's network view of pay not yet handed
 * over, as at one day. READ, DON'T RECOMPUTE — every owed day comes from
 * `artistArrears`, the same source as the Ops "Unpaid artists" tab (W4-RFIX), so
 * the two screens list the same artists, days and amounts:
 *
 * - Deferred: a written arrears record, at due − paid, whatever its age.
 * - Due, unsigned: a day the artist worked and finished (every committed session
 *   completed + 30 min, or the 6pm clock-off) with no signed payout covering it.
 *
 * A day an artist is still working (a session in the chair or still to come) is
 * not owed yet, so it is left out; their earlier owed days still show.
 */
export type OwedStatus = 'deferred' | 'due';

export interface OwedDay {
  dayIso: string;
  amountAud: number;
  /** Whole studio days between the work day and the as-at day. */
  ageDays: number;
  /** Past the arrears threshold (`ALERT_BANDS.payoutArrearsDays`). */
  escalated: boolean;
  status: OwedStatus;
}

export interface OutstandingPayRow {
  studioId: StudioId;
  artistId: string;
  artistName: string;
  /** Oldest first. */
  days: OwedDay[];
  /** 'due' when any owed day is worked but unsigned, else 'deferred' (all written arrears). */
  status: OwedStatus;
  owedAud: number;
}

export interface OutstandingPay {
  asAtDayIso: string;
  /** Oldest owed day first, then biggest debt. */
  rows: OutstandingPayRow[];
  totalAud: number;
}

export function outstandingPay(studioIds: readonly StudioId[], asAtDayIso: string): OutstandingPay {
  const brain = useBrainStore.getState();
  const asAt = dayKey(asAtDayIso);
  // "As at" the day: now on today, else the end of that day (never the future).
  const clockIso = useSimulationStore.getState().clockIso;
  const endOfDayMs = Date.parse(`${asAt}T23:59:59+10:00`);
  const nowIso = Date.parse(clockIso) < endOfDayMs ? clockIso : new Date(endOfDayMs).toISOString();

  const byArtist = new Map<string, OutstandingPayRow>();
  if (studioIds.length > 0) {
    for (const d of artistArrears({
      nowIso,
      studioIds,
      bookings: brain.bookings,
      artists: brain.artists,
      payoutRecords: brain.payoutRecords,
      salePayments: brain.salePayments,
    })) {
      const key = `${d.studioId}:${d.artistId}`;
      const r =
        byArtist.get(key) ??
        ({ studioId: d.studioId, artistId: d.artistId, artistName: d.artistName, days: [], status: 'deferred', owedAud: 0 } as OutstandingPayRow);
      r.days.push({ dayIso: d.dayIso, amountAud: d.amountAud, ageDays: d.ageDays, escalated: d.escalated, status: d.status });
      if (d.status === 'due') r.status = 'due';
      r.owedAud += d.amountAud;
      byArtist.set(key, r);
    }
  }

  const rows = [...byArtist.values()].sort(
    (a, b) => a.days[0]!.dayIso.localeCompare(b.days[0]!.dayIso) || b.owedAud - a.owedAud,
  );
  return { asAtDayIso: asAt, rows, totalAud: rows.reduce((sum, r) => sum + r.owedAud, 0) };
}
